import { startCase } from 'lodash'
import { useState } from 'react'
import {
  detectEnvironment,
  Environment,
  isConsole,
  isProductionEnv,
  LOG_ROCKET_APP_ID_ANDROID,
  LOG_ROCKET_APP_ID_CONSOLE,
  LOG_ROCKET_APP_ID_IOS,
  LOG_ROCKET_APP_ID_WEB,
} from 'shared/config/config'

import { Wrap } from '../../../components/Base/Wrap'
import { Button } from '../../../components/Button/Button'
import { SimpleDialog } from '../../../components/Dialog/SimpleDialog'
import { ExternalLink } from '../../../components/ExternalLink/ExternalLink'
import { formatDate } from '../../../helpers/formatters'
import { User } from '../../../models/user'
import { ProfileCard, ProfileCardItem } from './ProfileCard'
import { ProfileDeviceVersions } from './ProfileDeviceVersions'
import { UserAppPermissions } from './ProfileUserPermissions'

interface ProfilePersonalAccountInfoProps {
  user: User
  canEdit: boolean
}

export function ProfilePersonalAccountInfo({ user }: ProfilePersonalAccountInfoProps) {
  const [showFeatureFlags, setShowFeatureFlags] = useState(false)
  const [showPermissions, setShowPermissions] = useState(false)
  const flags = user.featureFlags || {}
  const activeFeatureFlags = Object.keys(flags).filter((flag) => flags[flag])

  const logrocketWebSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_WEB}/sessions?u=${user?.id}`
  const logrocketAndroidSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_ANDROID}/sessions?u=${user?.id}`
  const logrocketIosSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_IOS}/sessions?u=${user?.id}`
  const logrocketConsoleSessionsURI = `https://app.logrocket.com/${LOG_ROCKET_APP_ID_CONSOLE}/sessions?u=${user?.id}`

  return (
    <ProfileCard title="Account info">
      <ProfileCardItem label="Account Status">
        {startCase(user.defaultTeamMembership?.driverStatus?.toLocaleLowerCase())}
      </ProfileCardItem>
      {isConsole() && <ProfileCardItem label="User ID">{user.id}</ProfileCardItem>}
      {user.defaultTeamMembership?.driverStatusReason && (
        <ProfileCardItem label="Status Reason">
          {user.defaultTeamMembership.driverStatusReason}
        </ProfileCardItem>
      )}
      <ProfileCardItem label="Date joined">
        {user.dateJoined ? formatDate(user.dateJoined) : '-'}
      </ProfileCardItem>
      <ProfileCardItem label="Trailer permissions">
        {user.equipmentTypes?.map((e) => startCase(e.toLocaleLowerCase()))?.join(', ')}
      </ProfileCardItem>
      <ProfileCardItem label="App">
        <ProfileDeviceVersions user={user} />
      </ProfileCardItem>
      <ProfileCardItem label="Feature flags">
        <Button variant="text" onClick={() => setShowFeatureFlags(true)}>
          Show
        </Button>
      </ProfileCardItem>
      <ProfileCardItem label="Permissions">
        <Button variant="text" onClick={() => setShowPermissions(true)}>
          Show
        </Button>
      </ProfileCardItem>

      {showFeatureFlags && (
        <SimpleDialog isOpen onClose={() => setShowFeatureFlags(false)} alignText="left">
          <Wrap>{activeFeatureFlags?.join(', ')}</Wrap>
        </SimpleDialog>
      )}

      {showPermissions && (
        <SimpleDialog isOpen onClose={() => setShowPermissions(false)} alignText="left">
          <UserAppPermissions user={user} />
        </SimpleDialog>
      )}

      {isProductionEnv() && isConsole() && (
        <ProfileCardItem label="Logrocket">
          <>
            <ExternalLink variant="header3" href={logrocketWebSessionsURI}>
              Web
            </ExternalLink>
            {' • '}
            <ExternalLink variant="header3" href={logrocketAndroidSessionsURI}>
              Android
            </ExternalLink>
            {' • '}
            <ExternalLink variant="header3" href={logrocketIosSessionsURI}>
              iOS
            </ExternalLink>
            {' • '}
            <ExternalLink variant="header3" href={logrocketConsoleSessionsURI}>
              Console
            </ExternalLink>
          </>
        </ProfileCardItem>
      )}

      {isConsole() && (
        <ProfileCardItem label="Old console">
          <>
            <ExternalLink variant="header3" href={getOldConsoleUrl('customuser', user.id)}>
              User
            </ExternalLink>
            {user.defaultTeamMembership && (
              <>
                {' • '}
                <ExternalLink
                  variant="header3"
                  href={getOldConsoleUrl('teamuser', user.defaultTeamMembership.id)}
                >
                  Team User
                </ExternalLink>
                {' • '}
                <ExternalLink variant="header3" href={getOldConsoleUrl('team', user.team)}>
                  Team
                </ExternalLink>
              </>
            )}
          </>
        </ProfileCardItem>
      )}
    </ProfileCard>
  )
}

function getOrigin() {
  if (detectEnvironment() === Environment.DEVELOPMENT) {
    return 'http://localhost:8000'
  }

  return ''
}

function getOldConsoleUrl(recordType: string, recordId?: string) {
  if (!recordId) return ''

  return `${getOrigin()}/ct-admin/core/${recordType}/${recordId}/change/`
}
